/**
 * Logs view — Phase 2.3.a.
 *
 * Owns the Logs tab: failed payments, failed requests, report-jobs, and
 * generic job logs. Renders the appropriate table header and rows for the
 * selected log kind, paginates client-side, and opens the report-job
 * detail dialog.
 *
 * What stays in admin.js (injected via `configure({helpers})`):
 *   - `escapeHtml`, `openDialog` — generic.
 *
 * Boot contract: `admin.html` loads this script with the other view
 * modules. The IIFE registers `window.adminViews.logs`. The view-router
 * calls `init()` when the user opens the Logs tab; `mount()` wires the
 * filter / pagination / row-click listeners at admin.js boot to preserve
 * the original behavior (those listeners ran at script-load time).
 *
 * Phase 3.2.c: replaced `configure({helpers})` injection with direct
 * imports from `src/lib/format.js` + `src/lib/dialog.js`.
 *
 * Phase 3.2.d: dropped the `views.logs = {...}` self-registration. The
 * public surface is exported as a default object; `main.js` imports it
 * and writes `window.adminViews.logs` explicitly so the registry has a
 * single owner.
 */

import { escapeHtml, formatDateTimeFull, formatCurrency, formatNumber } from '../../src/lib/format.js';
import { openDialog } from '../../src/lib/dialog.js';
import * as api from '../api-client.js';
import * as notify from '../notifications.js';


const adminAPI = api;
const showFlash = (msg, type, ms) => notify.showFlash(msg, type, ms);

  // ---- DOM refs (lazy-resolved) ---------------------------------------
  let logsType, logsSearch, logsPrev, logsNext, logsPageInfo,
      logsThead, logsTbody, reportJobDlg, reportJobDetails, reportJobFilters;

  let domResolved = false;
  function resolveDom() {
    if (domResolved) return;
    domResolved = true;
    logsType = document.getElementById('logsType');
    logsSearch = document.getElementById('logsSearch');
    logsPrev = document.getElementById('logsPrev');
    logsNext = document.getElementById('logsNext');
    logsPageInfo = document.getElementById('logsPageInfo');
    logsThead = document.getElementById('logsThead');
    logsTbody = document.getElementById('logsTbody');
    reportJobDlg = document.getElementById('reportJobDlg');
    reportJobDetails = document.getElementById('reportJobDetails');
    reportJobFilters = document.getElementById('reportJobFilters');
  }

  // ---- State ----------------------------------------------------------
  let logsOffset = 0;
  let logsLimit = 20;
  let logsQuery = '';
  const reportJobsCache = new Map();

  function renderLogsHeader(kind) {
    if (kind === 'failed-payments') {
      logsThead.innerHTML = '<tr><th>Ref</th><th>Phone</th><th>Amount</th><th>Status</th><th>Checkout</th><th>Timestamp</th></tr>';
    } else if (kind === 'failed-requests') {
      logsThead.innerHTML = '<tr><th>ID</th><th>Phone</th><th>MAC</th><th>AP</th><th>Reason</th><th>Timestamp</th></tr>';
    } else if (kind === 'report-jobs') {
      logsThead.innerHTML = '<tr><th>ID</th><th>Report</th><th>Status</th><th>Requested by</th><th>Created</th><th>Finished</th><th>Rows</th><th>Expires</th><th>Actions</th></tr>';
    } else if (kind === 'audit') {
      logsThead.innerHTML = '<tr><th>Time</th><th>Actor</th><th>Action</th><th>Target</th><th>Changes</th><th>Meta</th></tr>';
    } else if (kind === 'role-changes') {
      logsThead.innerHTML = '<tr><th>Time</th><th>Actor</th><th>Change</th><th>Role</th><th>Target user</th><th>Entry hash</th></tr>';
    } else {
      logsThead.innerHTML = '<tr><th>ID</th><th>Job</th><th>Status</th><th>Started</th><th>Finished</th><th>Duration</th><th>Message</th></tr>';
    }
  }

  function renderLogs(kind, items, meta) {
    logsTbody.innerHTML = '';
    // The role-change log leads with a chain-integrity banner (green = verified,
    // red = a row was edited/deleted out of band).
    if (kind === 'role-changes' && meta) {
      const ok = meta.integrity_ok !== false;
      const banner = document.createElement('tr');
      banner.innerHTML = `<td colspan="6" style="padding:8px 10px; font-weight:600; color:${ok ? '#10b981' : '#ef4444'};">`
        + (ok
            ? `✓ Immutable chain verified — ${formatNumber(meta.total || 0)} entries`
            : `✗ TAMPER DETECTED — chain broken at entry #${escapeHtml(String(meta.integrity_broken_id))}`)
        + `</td>`;
      logsTbody.appendChild(banner);
    }
    if (!items || !items.length) {
      if (!(kind === 'role-changes' && meta)) logsTbody.innerHTML = '<tr><td style="color:var(--sub);">None</td></tr>';
      else logsTbody.appendChild(Object.assign(document.createElement('tr'), { innerHTML: '<td colspan="6" style="color:var(--sub);">No role changes yet.</td>' }));
      return;
    }
    if (kind === 'failed-payments') {
      items.forEach(it => {
        const tr = document.createElement('tr');
        tr.innerHTML = `<td>${escapeHtml(it.ref_id || '')}</td><td>${escapeHtml(it.user_phone || '')}</td><td>${it.amount != null ? formatCurrency(it.amount) : ''}</td><td>${escapeHtml(it.payment_status || '')}</td><td>${escapeHtml(it.checkout_status || '')}</td><td>${it.timestamp ? formatDateTimeFull(it.timestamp) : ''}</td>`;
        logsTbody.appendChild(tr);
      });
    } else if (kind === 'failed-requests') {
      items.forEach(it => {
        const tr = document.createElement('tr');
        tr.innerHTML = `<td>${escapeHtml(String(it.id || ''))}</td><td>${escapeHtml(it.phone_number || '')}</td><td>${escapeHtml(it.mac_address || '')}</td><td>${escapeHtml(it.ap_ref || '')}</td><td>${escapeHtml(it.reason || '')}</td><td>${it.timestamp ? formatDateTimeFull(it.timestamp) : ''}</td>`;
        logsTbody.appendChild(tr);
      });
    } else if (kind === 'report-jobs') {
      reportJobsCache.clear();
      items.forEach(it => {
        reportJobsCache.set(String(it.id), it);
        const tr = document.createElement('tr');
        const createdAt = it.created_at ? formatDateTimeFull(it.created_at) : '';
        const finishedAt = it.finished_at ? formatDateTimeFull(it.finished_at) : '';
        const expiresAt = it.expires_at ? formatDateTimeFull(it.expires_at) : '';
        tr.innerHTML = `<td>${escapeHtml(String(it.id || ''))}</td>`+
          `<td>${escapeHtml(it.report_key || '')}</td>`+
          `<td>${escapeHtml(it.status || '')}</td>`+
          `<td>${escapeHtml(String(it.requested_by_user_id || '—'))}</td>`+
          `<td>${createdAt}</td>`+
          `<td>${finishedAt}</td>`+
          `<td>${it.row_count != null ? formatNumber(it.row_count) : '—'}</td>`+
          `<td>${expiresAt || '—'}</td>`+
          `<td><button class="btn" data-report-job="${escapeHtml(String(it.id || ''))}">Details</button></td>`;
        logsTbody.appendChild(tr);
      });
    } else if (kind === 'audit') {
      items.forEach(it => {
        const tr = document.createElement('tr');
        const fmtJson = (v) => {
          if (v == null) return '—';
          try {
            const s = JSON.stringify(v);
            return s.length > 200 ? s.slice(0, 200) + '…' : s;
          } catch { return String(v); }
        };
        const actor = [it.actor_username, it.actor_type ? `(${it.actor_type})` : ''].filter(Boolean).join(' ') || (it.actor_id || '—');
        const target = [it.target_type, it.target_id].filter(Boolean).join(':') || '—';
        const changes = (it.old_values || it.new_values)
          ? `<span style="color:var(--sub);">old:</span> ${escapeHtml(fmtJson(it.old_values))}<br><span style="color:var(--sub);">new:</span> ${escapeHtml(fmtJson(it.new_values))}`
          : '—';
        tr.innerHTML = `<td>${it.occurred_at ? formatDateTimeFull(it.occurred_at) : ''}</td>`+
          `<td>${escapeHtml(actor)}</td>`+
          `<td>${escapeHtml(it.action || '')}</td>`+
          `<td>${escapeHtml(target)}</td>`+
          `<td style="font-family:monospace; font-size:12px; max-width:400px; word-break:break-all;">${changes}</td>`+
          `<td style="font-family:monospace; font-size:12px;">${escapeHtml(fmtJson(it.metadata))}</td>`;
        logsTbody.appendChild(tr);
      });
    } else if (kind === 'role-changes') {
      items.forEach(it => {
        const tr = document.createElement('tr');
        const assign = it.action === 'assign';
        const change = `<span style="font-weight:600; color:${assign ? '#10b981' : '#ef4444'};">${assign ? '＋ Granted' : '－ Revoked'}</span>`;
        const target = `${escapeHtml(it.target_username || '—')}${it.target_user_id ? ` <span style="color:var(--sub);">#${escapeHtml(String(it.target_user_id))}</span>` : ''}`;
        const hash = it.entry_hash ? String(it.entry_hash).slice(0, 12) : '';
        tr.innerHTML = `<td>${it.occurred_at ? formatDateTimeFull(it.occurred_at) : ''}</td>`+
          `<td>${escapeHtml(it.actor_username || it.actor_id || '—')}</td>`+
          `<td>${change}</td>`+
          `<td><code>${escapeHtml(it.role_name || '')}</code></td>`+
          `<td>${target}</td>`+
          `<td style="font-family:monospace; font-size:12px; color:var(--sub);" title="${escapeHtml(it.entry_hash || '')}">${escapeHtml(hash)}…</td>`;
        logsTbody.appendChild(tr);
      });
    } else {
      items.forEach(it => {
        const tr = document.createElement('tr');
        const dur = (it.duration_ms != null) ? `${formatNumber(it.duration_ms)} ms` : '';
        tr.innerHTML = `<td>${escapeHtml(String(it.id || ''))}</td>`+
          `<td>${escapeHtml(it.job_name || '')}</td>`+
          `<td>${escapeHtml(it.status || '')}</td>`+
          `<td>${it.started_at ? formatDateTimeFull(it.started_at) : ''}</td>`+
          `<td>${it.finished_at ? formatDateTimeFull(it.finished_at) : ''}</td>`+
          `<td>${dur}</td>`+
          `<td>${escapeHtml(it.message || '')}</td>`;
        logsTbody.appendChild(tr);
      });
    }
  }

  function getActiveKind() {
    const active = logsType && logsType.querySelector('button.toggle.active[data-kind]');
    return (active && active.dataset.kind) || 'failed-payments';
  }

  function setActiveKind(kind) {
    if (!logsType) return;
    logsType.querySelectorAll('button.toggle[data-kind]').forEach((btn) => {
      const isActive = btn.dataset.kind === kind;
      btn.classList.toggle('active', isActive);
      btn.setAttribute('aria-selected', isActive ? 'true' : 'false');
    });
  }

  async function loadLogs() {
    const kind = getActiveKind();
    renderLogsHeader(kind);
    const params = new URLSearchParams();
    params.set('limit', String(logsLimit)); params.set('offset', String(logsOffset));
    const q = (logsQuery || '').trim(); if (q) params.set('q', q);
    const url = `/api/admin/logs/${kind}?${params.toString()}`;
    try { const data = await adminAPI.fetch(url, { method: 'GET' }); renderLogs(kind, (data && data.items) || [], data); logsPageInfo.textContent = `${Math.floor(logsOffset/logsLimit)+1}`; }
    catch (err) { logsTbody.innerHTML = `<tr><td style="color:var(--sub);">${escapeHtml(err.message||'Error')}</td></tr>`; }
  }

  let mounted = false;
  function mount() {
    if (mounted) return;
    mounted = true;
    resolveDom();
    if (logsType) {
      logsType.addEventListener('click', (event) => {
        const btn = event.target.closest('button.toggle[data-kind]');
        if (!btn || !logsType.contains(btn)) return;
        if (btn.classList.contains('active')) return;
        setActiveKind(btn.dataset.kind);
        logsOffset = 0;
        loadLogs();
      });
    }
    if (logsSearch) logsSearch.addEventListener('input', (e) => { logsQuery = e.target.value || ''; logsOffset = 0; loadLogs(); });
    if (logsPrev) logsPrev.addEventListener('click', () => { logsOffset = Math.max(0, logsOffset - logsLimit); loadLogs(); });
    if (logsNext) logsNext.addEventListener('click', () => { logsOffset += logsLimit; loadLogs(); });
    if (logsTbody) {
      logsTbody.addEventListener('click', (event) => {
        const btn = event.target.closest('button[data-report-job]');
        if (!btn) return;
        const jobId = btn.dataset.reportJob;
        const job = reportJobsCache.get(String(jobId));
        if (!job || !reportJobDetails || !reportJobFilters || !reportJobDlg) return;
        reportJobDetails.innerHTML = '';
        const rows = [
          ['Report', job.report_key || '—'],
          ['Status', job.status || '—'],
          ['Requested by', job.requested_by_user_id || '—'],
          ['Rows', job.row_count != null ? formatNumber(job.row_count) : '—'],
          ['Created', job.created_at ? formatDateTimeFull(job.created_at) : '—'],
          ['Started', job.started_at ? formatDateTimeFull(job.started_at) : '—'],
          ['Finished', job.finished_at ? formatDateTimeFull(job.finished_at) : '—'],
          ['Expires', job.expires_at ? formatDateTimeFull(job.expires_at) : '—'],
          ['Error', job.error_message || '—'],
        ];
        rows.forEach(([label, value]) => {
          const card = document.createElement('div');
          card.className = 'crm-install-field';
          card.innerHTML = `<div class="label">${escapeHtml(label)}</div><div class="value">${escapeHtml(String(value || '—'))}</div>`;
          reportJobDetails.appendChild(card);
        });
        reportJobFilters.textContent = job.filters ? JSON.stringify(job.filters, null, 2) : '{}';
        openDialog(reportJobDlg);
      });
    }
  }

export default {
  mount,
  init: () => { mount(); loadLogs(); },
};
